/**
 * Franklin's analytics taxonomy — the only place an event name is allowed to be written.
 *
 * Rules (ratified 2026-09-09, binding):
 * - Names are `object_action`: snake_case, past tense, the noun first.
 * - PostHog cannot rename an event once it has been received, so this list is permanent.
 *   Adding a name is a ratification, never a keyboard decision.
 * - Properties are scalars only (string / number / boolean). Question text, completed
 *   answer text and original filenames are explicitly approved content fields (2026-09-21).
 *   Other events continue to use buckets and counts rather than arbitrary content.
 * - Completion events fire in the mutation's `onSuccess` or after the awaited call resolves —
 *   never inside `mutationFn` or before the result is known, or a React Query retry double-counts.
 *   Dialog-open intent events fire once in the explicit action handler, never on render.
 * - Who and where come from `identify` / `group` (see client.ts), never from properties.
 */

import { getPostHogRuntime } from "./client";

export type FranklinEvent =
  | { name: "document_signatures_viewed"; props: Record<string, never> }
  | { name: "document_signatures_checked"; props: { outcome: string; signature_count: number } }
  | { name: "building_activation_retried"; props: { status: "applied" | "pending" } }
  /** A activation — an account was created. */
  | { name: "signed_up"; props: { method: "password" | "google"; has_invite: boolean } }
  /** A activation — an existing account started a session. */
  | { name: "signed_in"; props: { method: "password" | "google" } }
  /** A activation — an organisation was created. */
  | { name: "organization_created"; props: { source: "onboarding" | "settings" } }
  /** A activation — invitations were sent to an organisation or a project. */
  | {
      name: "invite_sent";
      props: { role: string; invite_count: number; scope: "org" | "project" };
    }
  /** A activation — an invitation was accepted. */
  | { name: "invite_accepted"; props: { role: string; had_account: boolean } }
  /** A activation — a building was created. */
  | { name: "building_created"; props: Record<string, never> }
  | {
      name: "building_plan_changed";
      props: { tier: "free" | "starter" | "growing" | "scale"; status: "applied" | "pending" };
    }
  /** A activation — a project was created. */
  | { name: "project_created"; props: { source: "modal" | "documents" | "copy" } }
  /** A activation — an administrator changed a project's availability grouping. */
  | { name: "project_status_changed"; props: { status: "active" | "inactive" } }
  /**
   * B documents — one file finished uploading (one event per file).
   *
   * `source` is SURFACE-level, not gesture-level: it is bound once per mounting component,
   * so a surface that both accepts drops and opens the upload modal (ProjectScopedFolderView)
   * reports its one value for both. Read `drag` as a floor, never as a share of uploads.
   */
  | {
      name: "document_uploaded";
      props: {
        file_ext: string;
        size_bucket: "lt1mb" | "1_10mb" | "10_100mb" | "gt100mb";
        action: "created" | "versioned" | "skipped" | "resurrected";
        source: "drag" | "modal" | "chat";
      };
    }
  /** B documents — a file was downloaded. */
  | {
      name: "document_downloaded";
      props: {
        file_ext: string;
        surface: "table" | "viewer" | "version_history" | "signing" | "share";
      };
    }
  /**
   * B documents — a public share link was created.
   *
   * `expiry_days` is fractional, not whole: the picker's shortest option is 1 hour, which
   * rounds to 0 and would read as a missing value. The four values are 0.0417 / 1 / 7 / 30.
   */
  | { name: "share_link_created"; props: { expiry_days: number; item_count: number } }
  /** B documents — the DWG or IFC viewer was opened. */
  | {
      name: "model_viewer_opened";
      props: { viewer: "dwg_2d" | "ifc_3d"; entry: string; model_count: number };
    }
  /** B documents — one file successfully displayed, including each comparison side. */
  | {
      name: "document_viewed";
      props: {
        document_id: string;
        file_name: string;
        viewer: "dwg_2d" | "ifc_3d" | "document";
        surface: string;
      };
    }
  /**
   * B documents — two model versions were compared.
   *
   * One applied selection is one event, on both viewers. Deliberate consequence on IFC: the
   * Differences Overview modal computes a diff (one event) and its "Open in 3D" button then
   * runs the pair again in the viewer, glTF loads and all (a second event). That is two
   * comparisons, and it is counted as two — so `ifc_3d` counts a modal-then-3D journey twice
   * where `dwg_2d` has no equivalent path. Read the two viewers' rates against their own
   * `model_viewer_opened`, not against each other.
   */
  | {
      name: "drawing_compared";
      props: {
        viewer: "dwg_2d" | "ifc_3d";
        change_count_bucket: "0" | "1_10" | "11_100" | "gt100";
      };
    }
  /** C pm — an issue was created. */
  | {
      name: "issue_created";
      props: {
        source: string;
        has_assignee: boolean;
        has_due_date: boolean;
        discipline_count: number;
        attachment_count: number;
      };
    }
  /** C pm — an issue moved between statuses. */
  | {
      name: "issue_status_changed";
      props: { from_status: string; to_status: string; bulk: boolean };
    }
  /** C pm — the Kanban New status dialog was requested, not a saved status. */
  | { name: "issue_status_creation_opened"; props: { surface: "kanban" } }
  /** C pm — a comment was added to an issue. */
  | { name: "issue_comment_added"; props: { has_mention: boolean; has_attachment: boolean } }
  /** D collaboration — a signing session was started. */
  | {
      name: "signing_session_started";
      props: { signer_count: number; file_count: number; output_format: "asice" | "pdf" };
    }
  /** D collaboration — a signer signed a document. */
  | { name: "document_signed"; props: { signer_position: number; chain_size: number } }
  /** D collaboration — a message was sent to the assistant. */
  | {
      name: "assistant_message_sent";
      props: {
        surface: "sidebar_chat" | "unified_search";
        suggestion_used: boolean;
        attachment_count: number;
        question_text: string;
        exchange_id: string;
        thread_id?: string;
      };
    }
  /** D collaboration — the final answer returned, with the question it answers. */
  | {
      name: "assistant_response_received";
      props: {
        surface: "sidebar_chat" | "unified_search";
        question_text: string;
        answer_text: string;
        exchange_id: string;
        thread_id?: string;
        answer_message_id?: string;
      };
    };

/** Proposed contacts names. Keep the contacts gate shut until ratification. */
export type ProposedContactEvent =
  | { name: "contact_created"; props: { scope: "building" | "project" } }
  | { name: "contact_updated"; props: Record<string, never> }
  | { name: "contact_deleted"; props: Record<string, never> }
  | { name: "contact_method_copied"; props: { method: "phone" | "email" } };

const CONTACT_EVENTS_RATIFIED = false;

/** Every ratified event name, for the taxonomy test and for PostHog project setup. */
export const ANALYTICS_EVENTS = [
  "signed_up",
  "signed_in",
  "organization_created",
  "invite_sent",
  "invite_accepted",
  "building_created",
  "building_plan_changed",
  "building_activation_retried",
  "project_created",
  "project_status_changed",
  "document_uploaded",
  "document_downloaded",
  "share_link_created",
  "model_viewer_opened",
  "document_viewed",
  "drawing_compared",
  "issue_created",
  "issue_status_changed",
  "issue_status_creation_opened",
  "issue_comment_added",
  "signing_session_started",
  "document_signed",
  "assistant_message_sent",
  "assistant_response_received",
  // `satisfies` rejects a name that is not in the union; the taxonomy test holds the count.
] as const satisfies readonly FranklinEvent["name"][];

/**
 * Send one ratified event. A no-op whenever PostHog is disabled (no key, or a gate shut it off).
 *
 * Analytics is never allowed to break the action it is reporting on, so every failure —
 * including a client that is mid-teardown — is swallowed.
 */
export function track<E extends FranklinEvent["name"]>(
  name: E,
  props: Extract<FranklinEvent, { name: E }>["props"]
): void {
  try {
    const runtime = getPostHogRuntime();
    if (!runtime.enabled) return;
    runtime.client.capture(name, props);
  } catch {
    // Deliberately silent: a dropped event is always cheaper than a broken user action.
  }
}

/** Keep an async action in its original organisation; discard it after sign-out/user change. */
function bindCaptureToCurrentUser(organisationId?: string) {
  try {
    const { enabled, client } = getPostHogRuntime();
    if (!enabled) return () => undefined;
    const distinctId = client.get_distinct_id();
    const groups: Record<string, unknown> = { ...client.getGroups() };
    if (organisationId) groups.organisation = organisationId;
    return (name: string, props: Record<string, unknown>) => {
      try {
        if (client.get_distinct_id() !== distinctId) return;
        client.capture(name, { ...props, $groups: groups });
      } catch {
        // Reporting must not interrupt the action or its completion.
      }
    };
  } catch {
    return () => undefined;
  }
}

export function bindTrackToCurrentUser(organisationId?: string): typeof track {
  const bound = bindCaptureToCurrentUser(organisationId);
  return (name, props) => bound(name, props);
}

/** Bind at action start; no proposed contacts event reaches PostHog before ratification. */
export function bindProposedContactTrack(organisationId?: string) {
  if (!CONTACT_EVENTS_RATIFIED) {
    return (_event: ProposedContactEvent) => undefined;
  }
  const bound = bindCaptureToCurrentUser(organisationId);
  return (event: ProposedContactEvent) => {
    bound(event.name, event.props);
  };
}
